import React from 'react'
import axios from 'axios'
import moment from 'moment'
import { Grid, Table, Panel, Label } from 'react-bootstrap'

const baseUrl = '/api/reports'

// nl-NL throughout: a period is always a thousands separator here, never a
// decimal point (that's a comma) — mixing the two on one page reads as a bug.
// No decimals anywhere except the month-over-month comparison.
const fmtMs = (ms, maximumFractionDigits = 0) => (ms === null || ms === undefined
  ? '—'
  : `${ms.toLocaleString('nl-NL', { maximumFractionDigits })} ms`)

function Section({ title, help, children }) {
  return (
    <Panel bsStyle="info">
      <Panel.Heading>
        <Panel.Title componentClass="h3">{title}</Panel.Title>
      </Panel.Heading>
      <Panel.Body>
        {help && <p className="text-muted">{help}</p>}
        {children}
      </Panel.Body>
    </Panel>
  )
}

function Header({ report }) {
  return (
    <Section title={`Rapport ${report.month}`}>
      <Table condensed>
        <tbody>
          <tr><td>Maand</td><td>{report.month}</td></tr>
          <tr><td>Status</td><td>{report.final ? 'Definitief' : 'Lopend'}</td></tr>
          <tr>
            <td>Dekking</td>
            <td>
              {report.complete
                ? <Label bsStyle="success">Compleet</Label>
                : <Label bsStyle="warning">Partieel — {report.incompleteReason}</Label>}
            </td>
          </tr>
          <tr>
            <td>Meetvenster</td>
            <td>{moment.utc(report.window.from).format('YYYY-MM-DD')} t/m {moment.utc(report.window.to).subtract(1, 'day').format('YYYY-MM-DD')}</td>
          </tr>
          <tr><td>Berekend op</td><td>{moment.utc(report.generatedAt).format('YYYY-MM-DD HH:mm:ss')} UTC</td></tr>
          <tr><td>Aantal requests</td><td>{report.volume.totalRequests.toLocaleString('nl-NL')}</td></tr>
        </tbody>
      </Table>
    </Section>
  )
}

function Latency({ latency }) {
  if (!latency) {
    return <Section title="Percentielen"><p>Geen verkeer in dit rapport.</p></Section>
  }

  return (
    <Section
      title="Percentielen"
      help="P95 = 95% van de requests werd binnen deze tijd afgehandeld, de resterende 5% duurde langer. Hoe hoger het percentiel, hoe gevoeliger voor uitschieters — P99.9 raakt bijna alleen de traagste requests."
    >
      <Table striped bordered condensed>
        <thead>
          <tr>
            <th className="text-right">P50</th><th className="text-right">P75</th><th className="text-right">P90</th>
            <th className="text-right">P95</th><th className="text-right">P99</th><th className="text-right">P99.9</th>
            <th className="text-right">Gem.</th><th className="text-right">Min</th><th className="text-right">Max</th>
            <th className="text-right">StdDev</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="text-right">{fmtMs(latency.p50)}</td><td className="text-right">{fmtMs(latency.p75)}</td>
            <td className="text-right">{fmtMs(latency.p90)}</td><td className="text-right">{fmtMs(latency.p95)}</td>
            <td className="text-right">{fmtMs(latency.p99)}</td><td className="text-right">{fmtMs(latency.p999)}</td>
            <td className="text-right">{fmtMs(latency.avg)}</td><td className="text-right">{fmtMs(latency.min)}</td>
            <td className="text-right">{fmtMs(latency.max)}</td><td className="text-right">{fmtMs(latency.stdDev)}</td>
          </tr>
        </tbody>
      </Table>
    </Section>
  )
}

function Sla({ sla }) {
  return (
    <Section
      title="SLA-drempels"
      help="Contractuele doelen voor P95 en P99. Deze blijven getoond, ook als ze met ruime marge worden gehaald."
    >
      <Table striped bordered condensed>
        <thead>
          <tr><th>Metriek</th><th className="text-right">Doel</th><th className="text-right">Werkelijk</th><th>Resultaat</th></tr>
        </thead>
        <tbody>
          {sla.map(target => (
            <tr key={target.metric}>
              <td>{target.metric}</td>
              <td className="text-right">{fmtMs(target.targetMs)}</td>
              <td className="text-right">{fmtMs(target.actualMs)}</td>
              <td>
                <Label bsStyle={target.pass ? 'success' : 'danger'}>
                  {target.pass ? 'Gehaald' : 'Niet gehaald'}
                </Label>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Section>
  )
}

function ByEndpoint({ byEndpoint }) {
  return (
    <Section
      title="Per endpoint"
      help="Latency uitgesplitst per API-route."
    >
      <Table striped bordered condensed>
        <thead>
          <tr>
            <th>Endpoint</th><th className="text-right">Aantal</th><th className="text-right">Gem.</th>
            <th className="text-right">P90</th><th className="text-right">P95</th><th className="text-right">P99</th>
            <th className="text-right">Max</th>
          </tr>
        </thead>
        <tbody>
          {byEndpoint.map(row => (
            <tr key={row.baseUrl}>
              <td>{row.baseUrl}</td>
              <td className="text-right">{row.n.toLocaleString('nl-NL')}</td>
              <td className="text-right">{fmtMs(row.avg)}</td>
              <td className="text-right">{fmtMs(row.p90)}</td>
              <td className="text-right">{fmtMs(row.p95)}</td>
              <td className="text-right">{fmtMs(row.p99)}</td>
              <td className="text-right">{fmtMs(row.max)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Section>
  )
}

function ByStatusCode({ byStatusCode }) {
  return (
    <Section
      title="Per statusCode"
      help="Verdeling van alle requests over HTTP-statuscodes, inclusief requests waarvan de responstijd niet gemeten is."
    >
      <Table striped bordered condensed>
        <thead><tr><th>StatusCode</th><th className="text-right">Aantal</th><th className="text-right">Percentage</th></tr></thead>
        <tbody>
          {byStatusCode.map(row => (
            <tr key={row.statusCode}>
              <td>{row.statusCode === null ? 'onbekend' : row.statusCode}</td>
              <td className="text-right">{row.n.toLocaleString('nl-NL')}</td>
              <td className="text-right">{row.pct.toLocaleString('nl-NL', { maximumFractionDigits: 1 })}%</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Section>
  )
}

function SlowTail({ slowTail }) {
  return (
    <Section
      title={`Trage staart (> ${slowTail.thresholdMs} ms)`}
      help={`De "trage staart" is de kleine groep requests die veel langer duurt dan gebruikelijk — hier alles boven de ${slowTail.thresholdMs} ms. De naam komt van de vorm van de latency-verdeling: de meeste requests zitten dicht bij elkaar aan de snelle kant, en een dunne "staart" loopt uit naar de zeldzame, trage uitschieters. Gegroepeerd per endpoint en statuscode, om structureel trage plekken op te sporen.`}
    >
      <Table striped bordered condensed>
        <thead><tr><th>Endpoint</th><th>StatusCode</th><th className="text-right">Aantal</th><th className="text-right">Max</th></tr></thead>
        <tbody>
          {slowTail.groups.map(group => (
            <tr key={`${group.baseUrl}-${group.statusCode}`}>
              <td>{group.baseUrl}</td>
              <td>{group.statusCode === null ? 'onbekend' : group.statusCode}</td>
              <td className="text-right">{group.n.toLocaleString('nl-NL')}</td>
              <td className="text-right">{fmtMs(group.max)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Section>
  )
}

function WithoutDuration({ volume }) {
  return (
    <Section
      title="Uitgefilterde requests (geen gemeten responstijd)"
      help="Requests waarvan de responstijd niet gemeten is, tellen niet mee in de percentielen hierboven. Ze staan hier apart, uitgesplitst per statuscode, zodat ze niet uit beeld verdwijnen."
    >
      <Table striped bordered condensed>
        <thead><tr><th>StatusCode</th><th className="text-right">Aantal</th></tr></thead>
        <tbody>
          <tr>
            <td><strong>Totaal</strong></td>
            <td className="text-right"><strong>{volume.requestsWithoutDuration.toLocaleString('nl-NL')}</strong></td>
          </tr>
          {volume.withoutDurationByStatus.map(row => (
            <tr key={row.statusCode}>
              <td>{row.statusCode === null ? 'onbekend' : row.statusCode}</td>
              <td className="text-right">{row.count.toLocaleString('nl-NL')}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Section>
  )
}

function slowTailGroupKey(group) {
  return `${group.baseUrl} ${group.statusCode}`
}

// Compares the slow-tail groups of two reports by (baseUrl, statusCode):
// groups only in the current month are "nieuw", groups only in the previous
// month have resolved and are "verdwenen" — both are worth surfacing, not
// just groups present in both.
function slowTailComparisonRows(current, previous) {
  const currentGroups = (current.slowTail && current.slowTail.groups) || []
  const previousGroups = (previous.slowTail && previous.slowTail.groups) || []
  const previousByKey = new Map(previousGroups.map(group => [slowTailGroupKey(group), group]))
  const currentKeys = new Set(currentGroups.map(slowTailGroupKey))

  const rows = currentGroups.map((group) => {
    const previousGroup = previousByKey.get(slowTailGroupKey(group))
    const previousN = previousGroup ? previousGroup.n : 0

    return {
      baseUrl: group.baseUrl,
      statusCode: group.statusCode,
      previousN,
      n: group.n,
      delta: group.n - previousN,
      status: previousN === 0 ? 'nieuw' : null,
    }
  })

  const resolvedRows = previousGroups
    .filter(group => !currentKeys.has(slowTailGroupKey(group)))
    .map(group => ({
      baseUrl: group.baseUrl,
      statusCode: group.statusCode,
      previousN: group.n,
      n: 0,
      delta: -group.n,
      status: 'verdwenen',
    }))

  return [...rows, ...resolvedRows]
}

function SlowTailComparison({ report, previous }) {
  const rows = slowTailComparisonRows(report, previous)

  if (rows.length === 0) {
    return null
  }

  return (
    <>
      <p className="text-muted" style={{ marginTop: '15px' }}><strong>Trage staart t.o.v. vorige maand</strong></p>
      <Table striped bordered condensed>
        <thead>
          <tr>
            <th>Endpoint</th><th>StatusCode</th>
            <th className="text-right">Vorige maand</th><th className="text-right">Deze maand</th>
            <th className="text-right">Delta</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={slowTailGroupKey(row)}>
              <td>{row.baseUrl}</td>
              <td>{row.statusCode === null ? 'onbekend' : row.statusCode}</td>
              <td className="text-right">{row.previousN.toLocaleString('nl-NL')}</td>
              <td className="text-right">{row.n.toLocaleString('nl-NL')}</td>
              <td className="text-right">{row.delta >= 0 ? '+' : ''}{row.delta.toLocaleString('nl-NL')}</td>
              <td>
                {row.status === 'nieuw' && <Label bsStyle="warning">Nieuw</Label>}
                {row.status === 'verdwenen' && <Label bsStyle="success">Verdwenen</Label>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  )
}

function Comparison({ report, previous }) {
  if (previous === undefined) {
    return null // still loading
  }

  const help = 'Verschil met de vorige maand, om trends te signaleren (bv. een structurele verslechtering die in één maand niet opvalt).'

  if (previous === null) {
    return (
      <Section title="Vergelijking met vorige maand" help={help}>
        <p>n/a — geen vorige maand</p>
      </Section>
    )
  }

  const delta = (metric) => (report.latency && previous.latency
    ? report.latency[metric] - previous.latency[metric]
    : null)
  const deltaP95 = delta('p95')
  const deltaP99 = delta('p99')
  const deltaRequests = report.volume.totalRequests - previous.volume.totalRequests
  const deltaLabel = ms => (ms === null ? '—' : `${ms >= 0 ? '+' : ''}${fmtMs(ms, 1)}`)

  return (
    <Section title="Vergelijking met vorige maand" help={help}>
      {!previous.complete &&
        <p><Label bsStyle="warning">Let op: vorige maand ({previous.month}) is partieel — {previous.incompleteReason}</Label></p>}
      <Table condensed>
        <tbody>
          <tr><td>Delta P95</td><td className="text-right">{deltaLabel(deltaP95)}</td></tr>
          <tr><td>Delta P99</td><td className="text-right">{deltaLabel(deltaP99)}</td></tr>
          <tr><td>Delta aantal requests</td><td className="text-right">{deltaRequests >= 0 ? '+' : ''}{deltaRequests.toLocaleString('nl-NL')}</td></tr>
        </tbody>
      </Table>
      <SlowTailComparison report={report} previous={previous} />
    </Section>
  )
}

class ReportDetail extends React.Component {
  state = {
    report: null,
    previous: undefined,
    error: false,
  }

  componentDidMount() {
    this.fetchReport(this.props.match.params.month)
  }

  authHeaders = () => ({ Authorization: `Bearer ${this.props.auth.getAccessToken()}` })

  fetchReport = month => {
    const { isAuthenticated } = this.props.auth

    if (!isAuthenticated()) {
      return
    }

    axios({ url: `${baseUrl}/${month}`, method: 'GET', headers: this.authHeaders() })
      .then(response => {
        this.setState({ report: response.data, error: false })
        this.fetchPrevious(month)
      })
      .catch(error => this.setState({ report: null, error }))
  }

  fetchPrevious = month => {
    const previousMonth = moment.utc(month, 'YYYY-MM').subtract(1, 'month').format('YYYY-MM')

    axios({ url: `${baseUrl}/${previousMonth}`, method: 'GET', headers: this.authHeaders() })
      .then(response => this.setState({ previous: response.data }))
      .catch(() => this.setState({ previous: null }))
  }

  render() {
    const { report, previous } = this.state

    if (!report) {
      return null
    }

    return (
      <Grid fluid>
        <Header report={report} />
        <Latency latency={report.latency} />
        <Sla sla={report.sla} />
        <ByEndpoint byEndpoint={report.byEndpoint} />
        <ByStatusCode byStatusCode={report.byStatusCode} />
        <SlowTail slowTail={report.slowTail} />
        <WithoutDuration volume={report.volume} />
        <Comparison report={report} previous={previous} />
      </Grid>
    )
  }
}

export default ReportDetail
